import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Flame, Star, Trophy, Check, X, HelpCircle, Loader2, Zap,
  Calendar, ChevronRight, RotateCcw, Sparkles, Lock
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Inline mini-games for the daily challenge modal
const MiniScramble = ({ data, onComplete }) => {
  const [answers, setAnswers] = useState({});
  const [revealed, setRevealed] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = () => {
    setSubmitted(true);
    const items = data.map(q => ({
      id: q.id, answer: q.answer, points: q.points,
      user_answer: (answers[q.id] || '').trim(),
    }));
    onComplete({ items });
  };

  return (
    <div className="space-y-3">
      {data.map((q, i) => {
        const isCorrect = submitted && (answers[q.id] || '').toLowerCase().trim() === q.answer;
        return (
          <div key={q.id} className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${
            submitted ? (isCorrect ? 'border-emerald-400 bg-emerald-50' : 'border-red-300 bg-red-50') : 'border-slate-200'
          }`}>
            <span className="text-lg font-black tracking-[0.2em] text-fuchsia-600 font-mono min-w-[80px]">{q.scrambled}</span>
            <Input placeholder={`${q.length} letters`} value={answers[q.id] || ''}
              onChange={e => setAnswers(p => ({ ...p, [q.id]: e.target.value }))}
              disabled={submitted} maxLength={q.length + 2} className="text-base font-semibold flex-1"
              data-testid={`daily-scramble-${i}`} />
            {!submitted && !revealed[q.id] && (
              <button onClick={() => setRevealed(p => ({ ...p, [q.id]: true }))} className="text-slate-400 hover:text-fuchsia-500">
                <HelpCircle className="w-4 h-4" />
              </button>
            )}
            {revealed[q.id] && !submitted && <span className="text-xs text-fuchsia-500 italic max-w-[100px] truncate">{q.hint}</span>}
            {submitted && (isCorrect ? <Check className="w-5 h-5 text-emerald-500" /> : (
              <span className="text-xs text-red-500 font-medium">{q.answer}</span>
            ))}
          </div>
        );
      })}
      {!submitted && (
        <Button onClick={handleSubmit} className="w-full bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white" data-testid="daily-submit-btn">
          <Check className="w-4 h-4 mr-2" /> Check Answers
        </Button>
      )}
    </div>
  );
};

const MiniMissing = ({ data, onComplete }) => {
  const [answers, setAnswers] = useState({});
  const [revealed, setRevealed] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = () => {
    setSubmitted(true);
    const items = data.map(q => ({
      id: q.id, answer: q.answer, points: q.points,
      user_answer: (answers[q.id] || '').trim(),
    }));
    onComplete({ items });
  };

  return (
    <div className="space-y-3">
      {data.map((q, i) => {
        const isCorrect = submitted && (answers[q.id] || '').toLowerCase().trim() === q.answer;
        return (
          <div key={q.id} className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${
            submitted ? (isCorrect ? 'border-emerald-400 bg-emerald-50' : 'border-red-300 bg-red-50') : 'border-slate-200'
          }`}>
            <span className="text-lg font-black tracking-[0.3em] text-emerald-600 font-mono min-w-[80px]">{q.display}</span>
            <Input placeholder="Full word" value={answers[q.id] || ''}
              onChange={e => setAnswers(p => ({ ...p, [q.id]: e.target.value }))}
              disabled={submitted} maxLength={q.length + 2} className="text-base font-semibold flex-1"
              data-testid={`daily-missing-${i}`} />
            {!submitted && !revealed[q.id] && (
              <button onClick={() => setRevealed(p => ({ ...p, [q.id]: true }))} className="text-slate-400 hover:text-emerald-500">
                <HelpCircle className="w-4 h-4" />
              </button>
            )}
            {revealed[q.id] && !submitted && <span className="text-xs text-emerald-500 italic max-w-[100px] truncate">{q.hint}</span>}
            {submitted && (isCorrect ? <Check className="w-5 h-5 text-emerald-500" /> : (
              <span className="text-xs text-red-500 font-medium">{q.answer}</span>
            ))}
          </div>
        );
      })}
      {!submitted && (
        <Button onClick={handleSubmit} className="w-full bg-gradient-to-r from-emerald-500 to-green-500 text-white" data-testid="daily-submit-btn">
          <Check className="w-4 h-4 mr-2" /> Check Answers
        </Button>
      )}
    </div>
  );
};

const MiniCrossword = ({ data, onComplete }) => {
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = () => {
    setSubmitted(true);
    const items = data.map(q => ({
      id: q.id, answer: q.answer, points: q.points,
      user_answer: (answers[q.id] || '').trim(),
    }));
    onComplete({ items });
  };

  return (
    <div className="space-y-3">
      {data.map((q, i) => {
        const isCorrect = submitted && (answers[q.id] || '').toLowerCase().trim() === q.answer;
        return (
          <div key={q.id} className={`p-3 rounded-xl border-2 transition-all ${
            submitted ? (isCorrect ? 'border-emerald-400 bg-emerald-50' : 'border-red-300 bg-red-50') : 'border-slate-200'
          }`}>
            <p className="text-sm text-slate-600 mb-2">
              <span className={`text-xs px-1.5 py-0.5 rounded mr-1 ${q.direction === 'across' ? 'bg-amber-100 text-amber-700' : 'bg-orange-100 text-orange-700'}`}>
                {q.number}. {q.direction?.toUpperCase()}
              </span>
              {q.clue}
            </p>
            <div className="flex items-center gap-2">
              <Input placeholder={`${q.length} letters`} value={answers[q.id] || ''}
                onChange={e => setAnswers(p => ({ ...p, [q.id]: e.target.value }))}
                disabled={submitted} maxLength={q.length + 2} className="font-mono tracking-widest uppercase"
                data-testid={`daily-crossword-${i}`} />
              {submitted && (isCorrect ? <Check className="w-5 h-5 text-emerald-500" /> : (
                <span className="text-xs text-red-500 font-medium">{q.answer}</span>
              ))}
            </div>
          </div>
        );
      })}
      {!submitted && (
        <Button onClick={handleSubmit} className="w-full bg-gradient-to-r from-amber-500 to-orange-500 text-white" data-testid="daily-submit-btn">
          <Check className="w-4 h-4 mr-2" /> Check Answers
        </Button>
      )}
    </div>
  );
};

// ==================== DAILY CHALLENGE CARD ====================
export const DailyChallengeCard = ({ childId, apiHelper, theme = 'light', className = '', excludeBible = false }) => {
  const [challenge, setChallenge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [result, setResult] = useState(null);

  const isDark = theme === 'dark';

  useEffect(() => {
    fetchChallenge();
  }, [childId]);

  const fetchChallenge = async () => {
    try {
      setLoading(true);
      // Pass exclude_bible=true whenever the parent hasn't opted in to the Bible
      // curriculum so the daily puzzle never surfaces Bible content on their page.
      const params = excludeBible ? '?exclude_bible=true' : '';
      if (apiHelper) {
        const r = await apiHelper.get(`/word-puzzles/daily-challenge/${childId}${params}`);
        setChallenge(r.data);
      } else {
        const token = localStorage.getItem('littlelearners-token') || localStorage.getItem('token');
        const r = await fetch(`${API_URL}/api/word-puzzles/daily-challenge/${childId}${params}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await r.json();
        setChallenge(data);
      }
    } catch (e) {
      console.error('Failed to fetch daily challenge:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async (answers) => {
    try {
      let data;
      if (apiHelper) {
        const r = await apiHelper.post(`/word-puzzles/daily-challenge/${childId}/submit`, {
          child_id: childId,
          puzzle_type: challenge.puzzle_type,
          answers,
          time_spent: 0,
        });
        data = r.data;
      } else {
        const token = localStorage.getItem('littlelearners-token') || localStorage.getItem('token');
        const r = await fetch(`${API_URL}/api/word-puzzles/daily-challenge/${childId}/submit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ child_id: childId, puzzle_type: challenge.puzzle_type, answers, time_spent: 0 }),
        });
        data = await r.json();
      }
      setResult(data);
      toast.success(`+${data.xp_earned} XP earned!`);
    } catch (e) {
      toast.error('Failed to submit challenge');
      console.error(e);
    }
  };

  if (loading) {
    return (
      <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700/50' : 'border-slate-200'} ${className}`}>
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-amber-500" />
        </CardContent>
      </Card>
    );
  }

  if (!challenge) return null;

  // Already completed view
  if (challenge.completed && !playing) {
    return (
      <Card className={`border-2 ${isDark ? 'bg-emerald-900/20 border-emerald-500/30' : 'bg-emerald-50 border-emerald-300'} ${className}`}
        data-testid="daily-challenge-completed">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-xl flex items-center justify-center shadow-md flex-shrink-0">
              <Check className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <p className={`font-bold text-sm ${isDark ? 'text-emerald-300' : 'text-emerald-700'}`}>
                Daily Challenge Complete!
              </p>
              <p className={`text-xs ${isDark ? 'text-emerald-400/70' : 'text-emerald-600'}`}>
                Score: {challenge.previous_score}% · +{challenge.previous_xp} XP
              </p>
            </div>
            <div className="text-center flex-shrink-0">
              <div className="flex items-center gap-1">
                <Flame className={`w-4 h-4 ${challenge.streak.current >= 3 ? 'text-orange-400' : isDark ? 'text-slate-500' : 'text-slate-400'}`} />
                <span className={`text-lg font-black ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>{challenge.streak.current}</span>
              </div>
              <p className={`text-[10px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>streak</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Result view
  if (result) {
    return (
      <Card className={`border-2 ${isDark ? 'bg-amber-900/20 border-amber-500/30' : 'bg-amber-50 border-amber-300'} ${className}`}
        data-testid="daily-challenge-result">
        <CardContent className="p-5 text-center">
          <Trophy className={`w-10 h-10 mx-auto mb-2 ${result.score >= 70 ? 'text-amber-500' : isDark ? 'text-slate-400' : 'text-slate-500'}`} />
          <p className={`text-2xl font-black ${isDark ? 'text-white' : 'text-slate-800'}`}>{result.score}%</p>
          <p className={`text-sm ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>+{result.xp_earned} XP {result.streak_bonus > 0 && `(+${result.streak_bonus} streak bonus!)`}</p>
          <div className="flex items-center justify-center gap-1 mt-2">
            <Flame className="w-4 h-4 text-orange-400" />
            <span className={`text-sm font-bold ${isDark ? 'text-orange-300' : 'text-orange-600'}`}>{result.streak} day streak</span>
          </div>
          <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{result.message}</p>
        </CardContent>
      </Card>
    );
  }

  // Playing view
  if (playing && challenge.puzzle_data) {
    return (
      <Card className={`border-2 ${isDark ? 'bg-slate-800/80 border-indigo-500/30' : 'border-indigo-300'} ${className}`}
        data-testid="daily-challenge-playing">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <p className={`text-sm font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
              {challenge.day_name}'s Challenge: {challenge.subject_label}
            </p>
            <button onClick={() => setPlaying(false)} className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              <X className="w-4 h-4" />
            </button>
          </div>
          {challenge.puzzle_type === 'scramble' && <MiniScramble data={challenge.puzzle_data} onComplete={handleComplete} />}
          {challenge.puzzle_type === 'missing' && <MiniMissing data={challenge.puzzle_data} onComplete={handleComplete} />}
          {challenge.puzzle_type === 'crossword' && <MiniCrossword data={challenge.puzzle_data} onComplete={handleComplete} />}
        </CardContent>
      </Card>
    );
  }

  // Default: challenge available
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Card className={`border-2 cursor-pointer hover:scale-[1.01] transition-all ${
        isDark ? 'bg-gradient-to-r from-amber-900/30 to-orange-900/30 border-amber-500/40 hover:border-amber-400/60'
               : 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-300 hover:border-amber-400'
      } ${className}`}
        onClick={() => setPlaying(true)}
        data-testid="daily-challenge-card">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center shadow-md flex-shrink-0 animate-pulse">
              <Flame className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <p className={`font-bold ${isDark ? 'text-amber-300' : 'text-amber-800'}`}>
                Daily Challenge
                <span className={`text-xs ml-2 px-2 py-0.5 rounded-full ${isDark ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-200 text-amber-700'}`}>
                  {challenge.day_name}
                </span>
              </p>
              <p className={`text-xs mt-0.5 ${isDark ? 'text-amber-400/70' : 'text-amber-600'}`}>
                {challenge.subject_label} · {challenge.puzzle_type === 'scramble' ? 'Word Scramble' : challenge.puzzle_type === 'missing' ? 'Missing Letters' : 'Crossword'}
              </p>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              {challenge.streak.current > 0 && (
                <div className="text-center">
                  <div className="flex items-center gap-0.5">
                    <Flame className="w-3.5 h-3.5 text-orange-400" />
                    <span className={`text-sm font-black ${isDark ? 'text-orange-300' : 'text-orange-600'}`}>{challenge.streak.current}</span>
                  </div>
                  <p className={`text-[9px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>streak</p>
                </div>
              )}
              <ChevronRight className={`w-5 h-5 ${isDark ? 'text-amber-400' : 'text-amber-500'}`} />
            </div>
          </div>
          {challenge.streak.next_milestone && (
            <p className={`text-[10px] mt-2 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
              {challenge.streak.next_milestone.remaining} more day{challenge.streak.next_milestone.remaining !== 1 ? 's' : ''} to unlock +{challenge.streak.next_milestone.bonus} XP streak bonus!
            </p>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default DailyChallengeCard;
